<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';
import { getContactChannelNames } from 'dashboard/components-next/Contacts/helpers/contactChannel';

const props = defineProps({
  contact: {
    type: Object,
    required: true,
  },
  showAction: {
    type: Boolean,
    default: true,
  },
});

const emit = defineEmits(['sendMessage', 'showContact']);
const { t } = useI18n();

// Lấy thông tin kênh chat
const channelLabels = computed(() => getContactChannelNames(props.contact));

// Lấy thông tin đơn hàng
const orderLabel = computed(() => {
  const c = props.contact;
  if (!Array.isArray(c.orders) || !c.orders.length) return 'Chưa có đơn hàng';
  const first = c.orders[0];
  return first?.code || first?.name || '—';
});

// Lấy labels
const contactLabels = computed(() => {
  return props.contact.labels || [];
});

function onSendMessage() {
  emit('sendMessage', props.contact);
}

function onShowContact() {
  emit('showContact', props.contact.id);
}
</script>
<template>
      <div
        class="rounded-lg hover:shadow-sm dark:border bg-white dark:border-n-vars-black-bd/80 dark:bg-n-vars-black-br/30 p-3 w-full text-n-gray-3 flex flex-col gap-3 cursor-pointer"
        @click="onShowContact"
      >
        <div class="flex justify-between text-sm">
            <span class="text-n-gray-5 font-medium">Khách hàng</span>
            <span class="font-semibold text-n-gray-7 dark:text-n-gray-3">{{ contact.name || '—' }}</span>
        </div>
        <div class="flex justify-between text-sm">
          <span class="text-n-gray-5 font-medium">Kênh chat</span>
          <div class="flex gap-1 flex-wrap justify-end max-w-[60%]">
            <template v-if="channelLabels.length">
              <span
                v-for="label in channelLabels"
                :key="label"
                class="px-2 py-0.5 rounded text-xs bg-n-gray-1 text-n-gray-7 dark:bg-[#23282F] dark:text-white"
              >
                {{ label }}
              </span>
            </template>
            <span v-else class="font-medium text-n-gray-5">—</span>
          </div>
        </div>
        <div class="flex justify-between text-sm">
          <span class="text-n-gray-5 font-medium">Đơn hàng</span>
          <span 
            v-if="orderLabel !== 'Chưa có đơn hàng'"
            class="px-2 py-0.5 rounded text-xs bg-green-500 text-white font-medium"
          >
            {{ orderLabel }}
          </span>
          <span v-else class="text-n-gray-5 font-medium">{{ orderLabel }}</span>
        </div>
        <div class="flex justify-between text-sm">
          <span class="text-n-gray-5 font-medium">Nhãn</span>
          <div class="flex gap-1 flex-wrap">
            <div
              v-for="label in contactLabels.slice(0, 2)"
              :key="label.id"
              class="flex items-center gap-1"
            >
              <div
                class="w-2 h-2 rounded-sm"
                :style="{ backgroundColor: label.color }"
              />
              <span class="text-xs">{{ label.title }}</span>
            </div>
            <div
              v-if="contactLabels.length > 2"
              class="flex items-center gap-1"
            >
              <div class="w-2 h-2 rounded-sm bg-gray-400" />
              <span class="text-xs">+{{ contactLabels.length - 2 }}</span>
            </div>
          </div>
        </div>
        <div v-if="showAction" class="flex justify-between text-sm">
          <span class="text-n-gray-5 font-medium">Hành động</span>
          <button
            class="px-3 py-[6px] rounded-lg text-xs bg-grass-500 text-white"
            @click.stop="onSendMessage"
          >
            Gửi tin nhắn
          </button>
        </div>
      </div>
</template>
